import { getScaledValue } from './getScaledValue';
import { getTicks } from './getTicks';
import { ScaleWithValueType } from './SupportedScale';

export interface GetTickValuesParams<T> {
  /**
   * The scale to use to generate scaled values from ticks.
   */
  scale: ScaleWithValueType<T>;
  /**
   * The approximate number of ticks to generate.
   *
   * If `ticks` is specified, `ticks` will determine the actual ticks rendered instead of this prop.
   * `tickCount` may still be used to inform tick formatting if a custom `tickFormatter` is not
   * specified.
   */
  tickCount?: number;
  /**
   * A fixed set of ticks to use. If this value is supplied, it will be used instead of generating a
   * set of ticks.
   */
  ticks?: T[];
}

/**
 * Returns an array of ticks and their scaled values, given a scale and parameters for tick
 * selection. Ticks with no valid scaled value are excluded.
 */
export const getTickValues = <T>({ scale, tickCount, ticks: ticksParam }: GetTickValuesParams<T>): T[] => {
  const ticks = ticksParam ?? getTicks(scale, tickCount);

  // type assertions are necessary due to https://github.com/microsoft/TypeScript/issues/36390
  return (ticks as typeof ticks[number][]).reduce<T[]>((acc, tickValue) => {
    const scaledValue = getScaledValue(scale, tickValue as T);

    if (scaledValue !== undefined) {
      acc.push(tickValue as T);
    }

    return acc;
  }, []);
};
